import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Save,
  X,
  User
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  getDoc,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import useDataCache from '../../hooks/useDataCache';
import toast from 'react-hot-toast';

const TeacherAttendanceMarkingOptimized = ({ coachingId, onClose }) => {
  const { currentUser, userProfile } = useRealtime();
  const cache = useDataCache(5 * 60 * 1000); // 5-minute cache
  
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});
  const [existingAttendance, setExistingAttendance] = useState(null);

  useEffect(() => {
    if (coachingId) {
      loadTeachers();
    }
  }, [coachingId]);

  useEffect(() => {
    if (teachers.length > 0 && selectedDate) {
      loadExistingAttendance();
    }
  }, [teachers, selectedDate]);

  // OPTIMIZATION 1: Cache teacher list
  const loadTeachers = async () => {
    try {
      setLoading(true);
      
      // Check cache first
      const cacheKey = `teachers_${coachingId}`;
      const cachedTeachers = cache.get(cacheKey);
      
      if (cachedTeachers) {
        console.log('✅ Using cached teachers');
        setTeachers(cachedTeachers);
        
        // Initialize attendance data
        const initialAttendance = {};
        cachedTeachers.forEach(teacher => {
          initialAttendance[teacher.id] = 'present';
        });
        setAttendanceData(initialAttendance);
        setLoading(false);
        return;
      }
      
      // Load from Firestore
      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher'),
        where('coachingId', '==', coachingId)
      );
      
      const snapshot = await getDocs(teachersQuery);
      const teachersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Cache the result
      cache.set(cacheKey, teachersList);
      
      setTeachers(teachersList);
      
      // Initialize attendance data (default to absent)
      const initialAttendance = {};
      teachersList.forEach(teacher => {
        initialAttendance[teacher.id] = 'absent';
      });
      setAttendanceData(initialAttendance);
      
    } catch (error) {
      console.error('Error loading teachers:', error);
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingAttendance = async () => {
    try {
      const attendanceQuery = query(
        collection(db, 'teacherAttendance'),
        where('coachingId', '==', coachingId),
        where('date', '==', selectedDate)
      );
      
      const snapshot = await getDocs(attendanceQuery);
      
      let existingData = null;
      if (!snapshot.empty) {
        existingData = snapshot.docs[0].data();
        setExistingAttendance({ id: snapshot.docs[0].id, ...existingData });
      } else {
        // Fallback: check direct doc ID for more robust matching (path based storage)
        const attendanceDocId = `${coachingId}_${selectedDate}`;
        const attendanceDocRef = doc(db, 'teacherAttendance', attendanceDocId);
        const attendanceDoc = await getDoc(attendanceDocRef);
        if (attendanceDoc.exists()) {
          existingData = attendanceDoc.data();
          setExistingAttendance({ id: attendanceDoc.id, ...existingData });
        } else {
          setExistingAttendance(null);
        }
      }

      if (existingData) {
        const loadedAttendance = {};
        teachers.forEach(teacher => {
          const teacherRecord = existingData.teachers?.find(t => String(t.teacherId) === String(teacher.id));
          loadedAttendance[teacher.id] = teacherRecord?.status || 'absent';
        });
        setAttendanceData(loadedAttendance);
      } else {
        setExistingAttendance(null);
        const defaultAttendance = {};
        teachers.forEach(teacher => {
          defaultAttendance[teacher.id] = 'absent';
        });
        setAttendanceData(defaultAttendance);
      }
    } catch (error) {
      console.error('Error loading existing attendance:', error);
    }
  };

  const handleAttendanceChange = (teacherId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [teacherId]: status
    }));
  };

  // OPTIMIZATION 2: Batch writes instead of sequential writes
  const saveAttendance = async () => {
    try {
      setSaving(true);
      
      // Validation
      if (!coachingId || !currentUser?.uid || !userProfile) {
        throw new Error('Missing required data');
      }
      
      const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';
      const isCoachingAdmin = (userProfile?.role === 'coaching_admin' || userProfile?.role === 'coaching_staff') && userProfile?.coachingId === coachingId;
      const hasAttendanceAccess = userProfile?.role === 'coaching_admin' ||
        (userProfile?.role === 'coaching_staff' && userProfile?.accessibleSections?.includes('attendance'));
      
      if (!isAdmin && (!isCoachingAdmin || !hasAttendanceAccess)) {
        throw new Error('Permission denied');
      }
      
      if (teachers.length === 0) {
        throw new Error('No teachers to mark attendance for');
      }
      
      // Prepare attendance records
      const teacherAttendanceList = teachers.map(teacher => ({
        teacherId: teacher.id,
        teacherName: teacher.displayName || teacher.email,
        teacherEmail: teacher.email || '',
        status: attendanceData[teacher.id] || 'absent',
        markedAt: new Date()
      }));

      const attendanceRecord = {
        coachingId,
        date: selectedDate,
        teachers: teacherAttendanceList,
        markedBy: currentUser.uid,
        markedByName: userProfile.displayName || userProfile.email,
        createdAt: existingAttendance?.createdAt || new Date(),
        updatedAt: new Date()
      };
      
      // OPTIMIZATION: Use batch write for all operations
      const batch = writeBatch(db);
      
      // 1. Save/update main attendance record
      const attendanceId = `${coachingId}_${selectedDate}`;
      const attendanceRef = doc(db, 'teacherAttendance', attendanceId);
      batch.set(attendanceRef, attendanceRecord, { merge: true });
      
      // 2. Save individual teacher records in batch
      teachers.forEach(teacher => {
        const teacherAttendanceId = `${teacher.id}_${selectedDate}`;
        const teacherRef = doc(db, 'teacherDailyAttendance', teacherAttendanceId);
        
        const teacherAttendanceData = {
          teacherId: teacher.id,
          teacherName: teacher.displayName || teacher.email,
          teacherEmail: teacher.email || '',
          coachingId,
          date: selectedDate,
          status: attendanceData[teacher.id] || 'absent',
          markedAt: new Date(),
          markedBy: currentUser.uid,
          markedByName: userProfile.displayName || userProfile.email
        };
        
        batch.set(teacherRef, teacherAttendanceData);
      });
      
      // Commit all writes in a single batch (1 network call instead of N+1)
      await batch.commit();
      
      toast.success('Attendance saved successfully');
      onClose();
      
    } catch (error) {
      console.error('Error saving attendance:', error);
      
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Check your role and coaching access.');
      } else {
        toast.error(`Failed to save attendance: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const getAttendanceStats = () => {
    const total = teachers.length;
    const present = Object.values(attendanceData).filter(status => status === 'present').length;
    const absent = Object.values(attendanceData).filter(status => status === 'absent').length;
    const halfDay = Object.values(attendanceData).filter(status => status === 'half_day').length;
    
    return { total, present, absent, halfDay };
  };

  const stats = getAttendanceStats();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-900 dark:text-white">Loading teachers...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Mark Teacher Attendance
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Date Selector */}
          <div className="mt-4 flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Date:
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            {existingAttendance && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Already Marked
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.total}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">Total</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.present}</p>
              <p className="text-xs text-green-600 dark:text-green-400">Present</p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.halfDay}</p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">Half Day</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.absent}</p>
              <p className="text-xs text-red-600 dark:text-red-400">Absent</p>
            </div>
          </div>
        </div>

        {/* Teachers List */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-300px)]">
          {teachers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Teachers Found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                No teachers are registered for this coaching center.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {teachers.map((teacher) => (
                <div key={teacher.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {teacher.displayName || teacher.email}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {teacher.teacherProfile?.subject || 'No subject assigned'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleAttendanceChange(teacher.id, 'present')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        attendanceData[teacher.id] === 'present'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-green-100 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Present
                    </button>
                    <button
                      onClick={() => handleAttendanceChange(teacher.id, 'half_day')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        attendanceData[teacher.id] === 'half_day'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-yellow-100 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 inline mr-1" />
                      Half Day
                    </button>
                    <button
                      onClick={() => handleAttendanceChange(teacher.id, 'absent')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        attendanceData[teacher.id] === 'absent'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-red-100 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <XCircle className="w-4 h-4 inline mr-1" />
                      Absent
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={saveAttendance}
              disabled={saving || teachers.length === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Attendance</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherAttendanceMarkingOptimized;
